import {useMemo, useState} from 'react';

import classNames from 'classnames';

import {CalendarHeatmapGroup} from 'shells/calendar/calendar-heatmap/calendar-heatmap-group';
import type {CalendarHeatmapDay} from 'shells/calendar/calendar-heatmap/types';
import {Card} from 'shells/card/card';
import {SegmentedControl} from 'shells/card/segmented-control';
import {CalendarSquareRegularActivityIcon} from 'shells/icon/svg/calendar-square-regular-activity-icon';
import {CursorRegularIcon} from 'shells/icon/svg/cursor-regular-icon';
import {LoadingPage} from 'shells/loading-page/loading-page';

import type {AvailabilityReportFragment} from 'nutshell-graphql-types';

import {mapReportToHeatmapDates} from './helpers';
import type {ViewBookingHeatmapType} from './types';

import styles from './view-booking-heatmap.module.css';

interface Props {
    report: AvailabilityReportFragment | null;
    onDateClick: (day: CalendarHeatmapDay) => void;
    isLoading: boolean;
}

export function ViewBookingHeatmap(props: Props) {
    const [selectedValue, setSelectedValue] = useState<ViewBookingHeatmapType>('views');

    const mappedDates = useMemo(
        () => (props.report ? mapReportToHeatmapDates(props.report, selectedValue) : []),
        [props.report, selectedValue]
    );

    return (
        <Card hasBorder={true} hasShadow={true} fitHeight={true} borderRadius={8}>
            <div className='pad-24 flex flex-dir-col gap-24'>
                <div className={styles.container}>
                    <SegmentedControl
                        buttonOptions={[
                            {
                                value: 'views',
                                type: 'small',
                                title: 'Views',
                                icon: CursorRegularIcon,
                            },
                            {
                                value: 'bookings',
                                type: 'small',
                                title: 'Bookings',
                                icon: CalendarSquareRegularActivityIcon,
                            },
                        ]}
                        selectedValue={selectedValue}
                        setSelectedValue={setSelectedValue}
                    />
                </div>
                <div
                    className={classNames(
                        'overflow-x-auto flex gap-16',
                        styles['scroll-container']
                    )}
                >
                    <div className='flex-shrink'>
                        {props.isLoading ? (
                            <div
                                className={classNames(
                                    styles['loading-container'],
                                    'flex align-center justify-center'
                                )}
                            >
                                <LoadingPage size={48} />
                            </div>
                        ) : (
                            <CalendarHeatmapGroup
                                heatmapProps={{
                                    dates: mappedDates,
                                    onDateClick: props.onDateClick,
                                    columnGap: 8,
                                    rowGap: 4,
                                    colorVariant: selectedValue === 'views' ? 'pink' : 'blue',
                                }}
                                isHorizontal={true}
                                spacing={24}
                            />
                        )}
                    </div>
                </div>
            </div>
        </Card>
    );
}
